import endpoints from 'shared/store/endpoints'
import { ws } from 'shared/store/ws'
import type { OrbOrderShortDTO } from 'shared/types'
import type { OrbOrder } from './types'
import type { Dispatch } from 'redux'

// Follow an order
type ActionFollowOrder = {
    type: 'FOLLOW_ORDER',
    data: {
        order: OrbOrderShortDTO
    }
}

// Unfollow an order
type ActionUnfollowOrder = {
    type: 'UNFOLLOW_ORDER',
    data: {
        orderId: string
    }
}

// Set the last order id
type ActionUpdateLastOrder = {
    type: 'UPDATE_LAST_ORDER',
    data: {
        order: OrbOrderShortDTO
    }
}

// Replace all orders
type ActionReplaceOrders = {
    type: 'REPLACE_ORDERS',
    data: {
        orders: Array<OrbOrderShortDTO>
    }
}

// Update Order Status
export type ActionUpdateOrder = {
    type: 'ORB_EVENT_UPDATE_ORDER',
    data: { order: OrbOrder }
}

// Show or hide widget
type ActionToggleWidget = {
    type: 'TOGGLE_WIDGET',
    data: {
        active: boolean
    }
}

// Error
type ActionORBError = {
    type: 'ORB_ERROR',
    error: any
}

// Focus only for App
type ActionORBFocus = {
    type: 'ORB_FOCUS',
    isFocused: boolean
}

type ActionDiscardWidget = {
    type: 'DISCARD_WIDGET'
}

const actionUpdateOrder = (order: OrbOrder): ActionUpdateOrder => ({
    type: 'ORB_EVENT_UPDATE_ORDER',
    data: { order }
})

const actionUpdateLastOrder = (
    order: OrbOrderShortDTO
): ActionUpdateLastOrder => ({
    type: 'UPDATE_LAST_ORDER',
    data: { order }
})

const actionToggleWidget = (active: boolean): ActionToggleWidget => ({
    type: 'TOGGLE_WIDGET',
    data: { active }
})

const actionDiscardWidget = (): ActionDiscardWidget => ({
    type: 'DISCARD_WIDGET'
})

const actionReplaceOrders = (
    orders: Array<OrbOrderShortDTO>
): ActionReplaceOrders => ({
    type: 'REPLACE_ORDERS',
    data: { orders }
})

const actionFollowOrder = (order: OrbOrderShortDTO): ActionFollowOrder => ({
    type: 'FOLLOW_ORDER',
    data: { order }
})

const actionUnfollowOrder = (orderId: string): ActionUnfollowOrder => ({
    type: 'UNFOLLOW_ORDER',
    data: { orderId }
})

/**
    follow an order
    - qrCodeUrl : scanned code
 */
const followOrder = (qrCodeUrl: string, deviceId: string) => {
    return async (dispatch: Dispatch<any>) => {
        let formData = new FormData()
        formData.append('qrCodeUrl', qrCodeUrl)
        formData.append('deviceId', deviceId)
        try {
            /** On success get OrbOrderShortDTO */
            const response = await ws.post(endpoints.orb.follow, formData)
            if (response.data) {
                dispatch(actionFollowOrder(response.data))
            }
            return true
        } catch (error) {
            /** Server internal error or wrong code or already used code */
            return false
        }
    }
}

/**
    unfollow an order
    - orderId : technical id => id in database
 */
const unfollowOrder = (orderId: string, deviceId: string) => {
    return async (dispatch: Dispatch<any>) => {
        let formData = new FormData()
        formData.append('orderId', orderId)
        formData.append('deviceId', deviceId)
        try {
            /** Remove from the store before the request */
            dispatch(actionUnfollowOrder(orderId))
            await ws.post(endpoints.orb.unfollow, formData)
        } catch (error) {
            dispatch(actionError(error))
        }
    }
}

/**
    Fetch all followed orders 
 */
const getAllFollowedOrders = (deviceId: string) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            const response = await ws.get(endpoints.orb.all, {
                params: { deviceId }
            })
            const { data } = response
            if (data) {
                dispatch(actionReplaceOrders(data))
            }
        } catch (error) {
            dispatch(actionError(error))
        }
    }
}

/**
    Fetch last order
 */
const getLastOrder = (deviceId: string) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            const response = await ws.get(endpoints.orb.last, {
                params: { deviceId }
            })
            if (response.data) {
                dispatch(actionUpdateLastOrder(response.data))
            }
        } catch (error) {
            dispatch(actionError(error))
        }
    }
}

const toggleWidget = (active: boolean) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(actionToggleWidget(active))
    }
}

const discardWidget = () => {
    return (dispatch: Dispatch<any>) => {
        dispatch(actionDiscardWidget())
    }
}

const actionError = (error: any): ActionORBError => ({
    type: 'ORB_ERROR',
    error
})

const toggleOrbFocus = (isFocused: boolean): ActionORBFocus => ({
    type: 'ORB_FOCUS',
    isFocused
})

export {
    followOrder,
    unfollowOrder,
    getAllFollowedOrders,
    getLastOrder,
    actionUpdateOrder,
    toggleWidget,
    actionError,
    toggleOrbFocus,
    discardWidget
}

export type Action =
    | ActionFollowOrder
    | ActionUnfollowOrder
    | ActionUpdateLastOrder
    | ActionUpdateOrder
    | ActionReplaceOrders
    | ActionToggleWidget
    | ActionORBFocus
    | ActionORBError
    | ActionDiscardWidget
